/**
 * useRequireActiveSession — redirects to a fallback route when there is no
 * active purchase-flow session.
 *
 * "Active session" is defined as: the cart has at least one item. Cart items
 * are populated before the legacy `sessionId` arrives from the backend and are
 * the earliest reliable indicator that the user passed through the catalogue and
 * added something. An empty cart is functionally equivalent to no session — the
 * legacy step UIs would break just the same.
 *
 * The previous secondary guard `|| Boolean(s.draft.sessionId)` was removed:
 * `sessionId` is a transient BE-assigned value regenerated each time
 * `useEnsureRevampSession` runs. Using it as a session gate causes false
 * positives — a stale sessionId from a previous visit can keep a user on a
 * deep-linked step even when the cart is empty. Cart length is the canonical
 * authority.
 *
 * Mount at the top of Instructions, Billing, and Summary pages. The redirect
 * uses `replace: true` so the deep-linked URL is not left in browser history.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePfStore } from '../store'

/**
 * Redirects to `fallbackPath` if there is no active purchase-flow session.
 *
 * @param fallbackPath - Where to send a sessionless visitor. A plain string, not a
 *        `Path` constant: the flow has two mount points and the caller resolves
 *        the right one through `usePfPath` (see `pfBasePath`).
 *
 * @example
 * // In Instructions.page.tsx
 * useRequireActiveSession(pfPath(PfStepPath.CATALOGUE))
 */
export function useRequireActiveSession(fallbackPath: string): void {
  const navigate = useNavigate()

  const hasSession = usePfStore((s) => s.items.length > 0)

  useEffect(() => {
    if (!hasSession) {
      navigate(fallbackPath, { replace: true })
    }
  }, [hasSession, fallbackPath, navigate])
}
